import { supabase } from "./supabase";

/**
 * Human fixes to a song's chords, kept as an append-only history.
 *
 * A fix sets the chord on one beat. A chord sounding for eight beats is
 * therefore eight rows — which sounds wasteful until you want to move it half a
 * bar earlier, and it turns out renaming, extending and nudging are the same
 * operation on a different set of beats.
 *
 * Nothing is ever updated or deleted. The current value of a beat is its most
 * recent row, and undoing writes another row rather than erasing one, so the
 * editor can always show who changed what and put it back.
 */

export interface Correction {
  beat: number;
  chord: string;
  author: string | null;
}

export interface HistoryEntry {
  id: number;
  beat: number;
  chord: string | null;        // null means "back to what the engine heard"
  author: string | null;
  authorName: string | null;
  created_at: string;
}

/** The fixes in force right now. */
export async function loadCorrections(songId: string): Promise<Correction[]> {
  const { data } = await supabase
    .from("corrections_current").select("beat,chord,author").eq("song_id", songId);
  return (data ?? []) as Correction[];
}

/** Everything anyone has ever changed on this song, newest first. */
export async function loadHistory(songId: string, limit = 200): Promise<HistoryEntry[]> {
  const { data } = await supabase
    .from("corrections_log")
    .select("id,beat,chord,author,created_at")
    .eq("song_id", songId)
    .order("id", { ascending: false })
    .limit(limit);

  const rows = (data ?? []) as Omit<HistoryEntry, "authorName">[];
  const authors = [...new Set(rows.map((r) => r.author).filter(Boolean))] as string[];
  const names = new Map<string, string>();
  if (authors.length) {
    const { data: people } = await supabase
      .from("profiles").select("id,display_name,handle").in("id", authors);
    for (const person of people ?? []) {
      names.set(person.id, person.display_name || person.handle || "someone");
    }
  }
  return rows.map((row) => ({
    ...row,
    authorName: row.author ? names.get(row.author) ?? "someone" : null,
  }));
}

/** Set `chord` across a half-open beat range. */
export async function correctSpan(
  songId: string, from: number, to: number, chord: string | null,
): Promise<void> {
  const rows = [];
  for (let beat = from; beat < to; beat += 1) {
    rows.push({ song_id: songId, beat, chord });
  }
  if (!rows.length) return;
  const { error } = await supabase.from("corrections_log").insert(rows);
  if (error) throw error;
}

/** Put a beat range back to what the engine heard. */
export async function clearSpan(
  songId: string, from: number, to: number,
): Promise<void> {
  await correctSpan(songId, from, to, null);
}

/** Undo one entry by writing its opposite, since the log is never rewritten. */
export async function revertEntry(
  songId: string, entry: HistoryEntry, previous: string | null,
): Promise<void> {
  await correctSpan(songId, entry.beat, entry.beat + 1, previous);
}

/**
 * Shift a chord earlier or later by whole beats.
 *
 * Moving a chord is two edits, not one: the beats it vacates must go back to
 * whatever sounded before it, or the song is left with a hole.
 */
export async function nudgeSpan(
  songId: string,
  span: { beat: number; endBeat: number; chord: string },
  previousChord: string,
  beats: number,
): Promise<void> {
  const from = span.beat + beats;
  const to = span.endBeat + beats;
  if (from < 0) return;
  await correctSpan(songId, from, to, span.chord);
  if (beats < 0) {
    await correctSpan(songId, to, span.endBeat, span.chord);
  } else {
    await correctSpan(songId, span.beat, from, previousChord);
  }
}
